import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, ShieldCheck, Truck, CheckCircle2, Plane, ScanLine, FileBadge2 } from "lucide-react";
import { Wordmark, LogoMark } from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1542296332-2e4473faf563?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DESTINATIONS = ["South Korea", "Japan", "UAE", "Singapore", "United Kingdom", "United States", "Australia", "Germany"];

const FLOATING = [
  { icon: ScanLine, t: "Hub scan receipts", cls: "top-[22%] right-[10%]", delay: 0 },
  { icon: FileBadge2, t: "Branded AWB & POD", cls: "top-[46%] right-[18%]", delay: 1.4 },
  { icon: Plane, t: "Live door-to-door tracking", cls: "top-[34%] right-[4%]", delay: 0.7 },
];

export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background overflow-x-hidden">
      <div className="relative hidden lg:block overflow-hidden bg-[#070C16] grain">
        <img src={HERO} alt="Cargo plane" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#070C16]/60 via-[#0B1B4A]/80 to-[#070C16]" />
        <div className="pointer-events-none absolute -top-20 -left-10 h-80 w-80 rounded-full bg-primary/40 blur-3xl animate-blob" />
        <div className="pointer-events-none absolute bottom-10 right-0 h-72 w-72 rounded-full bg-brand-red/25 blur-3xl animate-blob" style={{ animationDelay: "5s" }} />
        <div className="pointer-events-none absolute top-1/2 left-1/3 h-56 w-56 rounded-full bg-cyan-400/20 blur-3xl animate-blob" style={{ animationDelay: "9s" }} />

        {FLOATING.map((f) => (
          <motion.div key={f.t} className={`absolute ${f.cls} flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-xs font-semibold text-white backdrop-blur-md shadow-lg`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: [0, -8, 0] }} transition={{ opacity: { delay: 0.4 + f.delay * 0.3 }, y: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: f.delay } }}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white"><f.icon className="h-3.5 w-3.5" /></span>{f.t}
          </motion.div>
        ))}

        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex w-fit items-center gap-2.5" data-testid="auth-logo-link">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/[.06] ring-1 ring-white/10 p-0.5 animate-floaty">
              <LogoMark className="h-full w-full" />
            </div>
            <Wordmark className="text-2xl" light />
          </Link>
          <div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-mono-label font-semibold uppercase tracking-[0.25em] text-white/80 backdrop-blur">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
              Every order, under control
            </motion.div>
            <motion.h2 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display text-4xl xl:text-5xl font-extrabold leading-[1.05] max-w-md">
              Every parcel, tracked <span className="bg-gradient-to-r from-cyan-300 via-white to-cyan-200 bg-clip-text text-transparent">door to door.</span>
            </motion.h2>
            <div className="mt-8 space-y-3">
              {[
                { icon: Package, t: "Book & track orders in real time" },
                { icon: Truck, t: "Smart courier dispatch & hub scanning" },
                { icon: ShieldCheck, t: "Role-based visibility for every team" },
              ].map((f, i) => (
                <motion.div key={f.t} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-center gap-3 text-white/90">
                  <CheckCircle2 className="h-5 w-5 text-cyan-300" />
                  <span className="text-sm">{f.t}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-10 overflow-hidden border-t border-white/10 pt-4">
              <div className="text-[10px] font-mono-label uppercase tracking-[0.25em] text-white/50 mb-2">India → the world</div>
              <div className="flex w-max animate-marquee gap-8 text-xs font-semibold text-white/70">
                {[...DESTINATIONS, ...DESTINATIONS].map((d, i) => <span key={i} className="flex items-center gap-2"><Plane className="h-3 w-3 -rotate-45 text-cyan-300" />{d}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-6 sm:p-12">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center gap-2.5 lg:hidden" data-testid="auth-logo-link-mobile">
            <LogoMark className="h-11 w-11 shrink-0" />
            <Wordmark className="text-2xl whitespace-nowrap" />
          </Link>
          <div className="panel p-7 sm:p-9">
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{title}</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>
            <div className="mt-7">{children}</div>
          </div>
          {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
        </motion.div>
      </div>
    </div>
  );
}
